import React, { useState } from 'react';
import { useApolloClient, useFragment } from '@apollo/client';
import useAppSelector from '../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../components/Menu';
import { BEACON_TOPIC_SIDEBAR } from '../constants/onboarding';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostType } from '../graphql/generated/graphql';
import drillDownIcon from '../icons/non-prefilled/16x16/drillDown.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import TutorialBeacon from '../onboarding/tutorial/TutorialBeacon';
import { PostIcon } from '../post/PostIcon';
import { TopicIcon } from '../topic/TopicIcon';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import sortTopic from '../topic/utils/sortTopic';
import postPath, { createPostPath } from '../utils/url/postPath';
import topicPath from '../utils/url/topicPath';
import RemoveFromSectionMenuItem from './RemoveFromSectionMenuItem';
import * as styles from './SidebarItems.module.scss';
import SidebarRow, { SidebarRowRightContent } from './SidebarRow';
import SidebarSectionAnimation from './SidebarSectionAnimation';
import TopicItemMenuItems from './TopicItemMenuItems';
import sortSidebarItems from './sortSidebarItems';

export const POST_FRAGMENT = gql(`
  fragment SidebarItems_post on SlimPostFields {
    id
    type
    title
    ...PostIcon_post
  }
`);

type PostFragment = DocumentType<typeof POST_FRAGMENT>;

interface PostItemProps {
  post: PostFragment;
  sectionId: string;
}

const PostItem = ({ post, sectionId }: PostItemProps) => {
  const client = useApolloClient();
  const postFragment = useFragment({
    fragment: POST_FRAGMENT,
    fragmentName: 'PostIcon_post',
    from: client.cache.identify(post) ?? null,
  });

  const cachedPost = postFragment.complete ? postFragment.data : post;

  return (
    <li key={post.id}>
      <SidebarRow
        level={1}
        icon={<PostIcon post={cachedPost} />}
        to={postPath(post)}
        type={(() => {
          if (cachedPost.archivedAt) {
            return 'archived';
          }

          if (cachedPost.type === PostType.Template && cachedPost.publishedAt) {
            return 'template';
          }

          if (cachedPost.type === PostType.Post && cachedPost.publishedAt) {
            return 'post';
          }

          return 'draft';
        })()}
        menuItems={
          cachedPost.type === PostType.Template ? (
            <>
              <MenuItem
                icon={postIcon}
                to={createPostPath({ templateId: post.id })}
              >
                Use template
              </MenuItem>
              <RemoveFromSectionMenuItem
                sectionId={sectionId}
                itemId={post.id}
              />
              <MenuSeparator />
              <MenuItem icon={drillDownIcon} to={postPath(post)}>
                View template
              </MenuItem>
            </>
          ) : (
            <>
              <RemoveFromSectionMenuItem
                sectionId={sectionId}
                itemId={post.id}
              />
              <MenuSeparator />
              <MenuItem icon={drillDownIcon} to={postPath(post)}>
                View post
              </MenuItem>
            </>
          )
        }
        rightContent={<SidebarRowRightContent expandState="none" />}
      >
        {post.title}
      </SidebarRow>
    </li>
  );
};

interface TopicItemProps {
  topic: AllTopicsTreeListTopic;
  path: string[];
  expandedTopics?: Record<string, boolean>;
  activeTopics: Record<string, boolean>;
  sectionId: string;
  selectedSectionId: string | null;
  forcedExpandedKeys: Record<string, boolean>;
  setForceExpandedKeys: (keys: Record<string, boolean>) => void;
}

const TopicItem = ({
  topic,
  path,
  expandedTopics,
  activeTopics,
  sectionId,
  selectedSectionId,
  forcedExpandedKeys,
  setForceExpandedKeys,
}: TopicItemProps) => {
  const { childTopics, id } = topic;
  const childPath = [...path, id];
  const key = childPath.join('.');
  const isForceExpanded = !expandedTopics || forcedExpandedKeys[key];
  const isExpanded =
    isForceExpanded !== undefined
      ? isForceExpanded
      : expandedTopics?.[id] || activeTopics[id];

  const isActive = activeTopics[id];

  const childComponent =
    isExpanded && childTopics.length > 0 ? (
      <ul className={styles.list}>
        {childTopics.toSorted(sortTopic).map((t) => (
          <TopicItem
            key={t.id}
            topic={t}
            path={childPath}
            expandedTopics={expandedTopics}
            activeTopics={activeTopics}
            sectionId={sectionId}
            forcedExpandedKeys={forcedExpandedKeys}
            setForceExpandedKeys={setForceExpandedKeys}
            selectedSectionId={selectedSectionId}
          />
        ))}
      </ul>
    ) : null;

  const getExpandState = () => {
    if (!expandedTopics || childTopics.length === 0) return 'none';
    return isExpanded ? 'expanded' : 'collapsed';
  };

  return (
    <TutorialBeacon
      key={key}
      id={BEACON_TOPIC_SIDEBAR}
      hide={!isActive}
      placement="right"
    >
      <li aria-label={topic.name}>
        <SidebarRow
          level={path.length + 1}
          icon={<TopicIcon size={16} topic={topic} />}
          to={topicPath(topic)}
          type="topic"
          onClick={() => {
            if (selectedSectionId != null) {
              setForceExpandedKeys({
                ...forcedExpandedKeys,
                [key]: !isExpanded,
              });
            }
          }}
          menuItems={<TopicItemMenuItems topic={topic} sectionId={sectionId} />}
          rightContent={
            <SidebarRowRightContent
              expandState={getExpandState()}
              onExpandClick={() =>
                setForceExpandedKeys({
                  ...forcedExpandedKeys,
                  [key]: !isExpanded,
                })
              }
            />
          }
          active={isActive}
        >
          {topic.name}
        </SidebarRow>
        <SidebarSectionAnimation sectionKey={id} expanded={!!isExpanded}>
          {childComponent}
        </SidebarSectionAnimation>
      </li>
    </TutorialBeacon>
  );
};

interface SidebarItemsProps {
  activeTopics: Record<string, boolean>;
  expandedTopics?: Record<string, boolean>;
  posts?: PostFragment[];
  topics: AllTopicsTreeListTopic[];
  orderIds?: string[];
  sectionId: string;
}

type RenderItem =
  | (AllTopicsTreeListTopic & { sidebarType: 'topic' })
  | (PostFragment & { sidebarType: 'post' });

const SidebarItems = ({
  activeTopics,
  expandedTopics,
  posts = [],
  topics,
  orderIds,
  sectionId,
}: SidebarItemsProps) => {
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const [forcedExpandedKeys, setForceExpandedKeys] = useState<
    Record<string, boolean>
  >({});

  const combined: RenderItem[] = [
    ...topics.map((topic) => ({ ...topic, sidebarType: 'topic' as const })),
    ...posts.map((post) => ({ ...post, sidebarType: 'post' as const })),
  ];

  const items = Array.isArray(orderIds)
    ? sortSidebarItems(combined, orderIds)
    : combined;

  return (
    <ul className={styles.list}>
      {items.map((item) => {
        if (item.sidebarType === 'post') {
          return <PostItem key={item.id} post={item} sectionId={sectionId} />;
        }
        if (item.sidebarType === 'topic') {
          return (
            <TopicItem
              key={item.id}
              topic={item}
              path={[]}
              expandedTopics={expandedTopics}
              activeTopics={activeTopics}
              sectionId={sectionId}
              selectedSectionId={selectedSectionId}
              forcedExpandedKeys={forcedExpandedKeys}
              setForceExpandedKeys={setForceExpandedKeys}
            />
          );
        }
        return null;
      })}
    </ul>
  );
};

export default SidebarItems;
